import { useState } from "react";
import { setStorageMode, setGDriveToken } from "../storage";

const STORAGE_OPTIONS = [
  {
    id: "local",
    icon: "📱",
    title: "This Device Only",
    subtitle: "Local Storage",
    desc: "Data saved only on this phone/device. No internet needed. Set a PIN to encrypt it. If you forget the PIN, local encrypted data cannot be recovered without a backup.",
    pros: ["✅ Free-safe default", "✅ No login required", "✅ Works fully offline"],
    cons: ["⚠️ Backup manually", "⚠️ Forgot PIN = no recovery"],
    color: "#0284C7",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    recommended: true
  },
  {
    id: "firebase",
    icon: "☁️",
    title: "FinVault Cloud",
    subtitle: "Firebase Server (Mumbai)",
    desc: "Optional cloud sync for backup and multi-device use. Use this only when you need online sync.",
    pros: ["✅ Sync across all devices", "✅ Auto backup", "✅ Works after reinstall"],
    cons: ["⚠️ Requires login", "⚠️ Uses Firebase quota"],
    color: "#4F46E5",
    bg: "#EEF2FF",
    border: "#C7D2FE"
  },
  {
    id: "gdrive",
    icon: "🟢",
    title: "My Google Drive",
    subtitle: "Your Personal Drive",
    desc: "Data saved as files in YOUR Google Drive account. You have full control — view, edit, delete anytime.",
    pros: ["✅ Your Drive, your control", "✅ View data as files", "✅ 15GB free storage"],
    cons: ["⚠️ Requires Google login", "⚠️ Slightly slower saves"],
    color: "#059669",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    comingSoon: true
  }
];

export default function StorageSetup({ onDone, dark = false }) {
  const [selected, setSelected]   = useState("local");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const selectableStorageIds = STORAGE_OPTIONS.filter(opt => !opt.comingSoon).map(opt => opt.id);
  const darkStorageCss = dark ? `
.storage-setup-screen[data-theme="dark"]{background:linear-gradient(160deg,#070B16 0%,#0F172A 58%,#111827 100%)!important;color:#F8FAFC!important;}
.storage-setup-screen[data-theme="dark"] [style*="background:white"],
.storage-setup-screen[data-theme="dark"] [style*="background: white"],
.storage-setup-screen[data-theme="dark"] [style*="background:#F8FAFC"],
.storage-setup-screen[data-theme="dark"] [style*="background: #F8FAFC"],
.storage-setup-screen[data-theme="dark"] [style*="background:#F8FAFF"],
.storage-setup-screen[data-theme="dark"] [style*="background: #F8FAFF"]{background:#0F172A!important;border-color:#334155!important;color:#F8FAFC!important;}
.storage-setup-screen[data-theme="dark"] [style*="color:#1E1B4B"],
.storage-setup-screen[data-theme="dark"] [style*="color: #1E1B4B"]{color:#F8FAFC!important;}
.storage-setup-screen[data-theme="dark"] [style*="color:#64748B"],
.storage-setup-screen[data-theme="dark"] [style*="color: #64748B"],
.storage-setup-screen[data-theme="dark"] [style*="color:#7C8DB5"],
.storage-setup-screen[data-theme="dark"] [style*="color: #7C8DB5"],
.storage-setup-screen[data-theme="dark"] [style*="color:#94A3B8"],
.storage-setup-screen[data-theme="dark"] [style*="color: #94A3B8"]{color:#CBD5E1!important;}
.storage-setup-screen[data-theme="dark"] [style*="background:#EFF6FF"],
.storage-setup-screen[data-theme="dark"] [style*="background: #EFF6FF"],
.storage-setup-screen[data-theme="dark"] [style*="background:#EEF2FF"],
.storage-setup-screen[data-theme="dark"] [style*="background: #EEF2FF"],
.storage-setup-screen[data-theme="dark"] [style*="background:#F0FDF4"],
.storage-setup-screen[data-theme="dark"] [style*="background: #F0FDF4"]{background:rgba(30,41,59,.88)!important;}
.storage-setup-screen[data-theme="dark"] [style*="background:#FFF7ED"],
.storage-setup-screen[data-theme="dark"] [style*="background: #FFF7ED"]{background:rgba(245,158,11,.16)!important;border-color:rgba(251,191,36,.35)!important;color:#FCD34D!important;}
.storage-setup-screen[data-theme="dark"] [style*="background:#FEF2F2"],
.storage-setup-screen[data-theme="dark"] [style*="background: #FEF2F2"]{background:rgba(239,68,68,.16)!important;border-color:rgba(248,113,113,.35)!important;color:#FCA5A5!important;}
` : "";

  const handleContinue = async () => {
    if (!selected) return;
    setLoading(true); setError("");

    if (selected === "gdrive") {
      try {
        // Use Google Identity Services popup for Drive access
        const SCOPES = "https://www.googleapis.com/auth/drive.file";
        const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!CLIENT_ID) {
          setError("Google Drive setup is not configured yet. Please use FinVault Cloud or Device Only.");
          setLoading(false);
          return;
        }
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES,
          callback: (tokenResponse) => {
            if (tokenResponse.error) {
              setError("Google Drive access was denied. Please try again.");
              setLoading(false);
              return;
            }
            setGDriveToken(tokenResponse.access_token);
            setStorageMode("gdrive");
            setLoading(false);
            onDone("gdrive");
          },
        });
        client.requestAccessToken({ prompt: "consent" });
      } catch(e) {
        setError("Google Drive is not available right now. Please use FinVault Cloud or Device Only.");
        setLoading(false);
      }
      return;
    }

    setStorageMode(selected);
    setLoading(false);
    onDone(selected);
  };

  const handleStorageKeyDown = (event, currentId) => {
    const direction = ["ArrowRight", "ArrowDown"].includes(event.key)
      ? 1
      : ["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 0;
    if (!direction) return;

    event.preventDefault();
    const currentIndex = selectableStorageIds.indexOf(currentId);
    const nextIndex = (currentIndex + direction + selectableStorageIds.length) % selectableStorageIds.length;
    const nextId = selectableStorageIds[nextIndex];
    setSelected(nextId);
    requestAnimationFrame(() => document.getElementById(`storage-option-${nextId}`)?.focus());
  };

  return (
    <div className="storage-setup-screen" data-theme={dark ? "dark" : "light"} style={{
      minHeight:"100vh", background:"linear-gradient(160deg,#EEF2FF 0%,#F0FDF4 100%)",
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", padding:"24px 20px",
      fontFamily:"'Nunito',sans-serif"
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');${darkStorageCss}`}</style>

      {/* Header */}
      <div style={{textAlign:"center",marginBottom:28,maxWidth:400}}>
        <img
          src="/icons/icon-128.png"
          alt="FinVault"
          width="72"
          height="72"
          style={{width:72,height:72,borderRadius:22,objectFit:"cover",margin:"0 auto 12px",display:"block",boxShadow:"0 18px 36px rgba(79,70,229,0.26)"}}
        />
        <div style={{fontSize:24,fontWeight:900,color:"#1E1B4B",letterSpacing:-0.5}}>
          Choose Free-Safe Storage
        </div>
        <div style={{fontSize:13,color:"#7C8DB5",marginTop:8,lineHeight:1.6}}>
          Local mode is recommended for a free app. Cloud sync is optional when you need backup across devices.
        </div>
      </div>

      {/* Options */}
      <div role="radiogroup" aria-label="Choose where FinVault stores your data"
        style={{width:"100%",maxWidth:420,display:"flex",flexDirection:"column",gap:12,marginBottom:24}}>
        {STORAGE_OPTIONS.map(opt => (
          <button key={opt.id} type="button" role="radio"
            id={`storage-option-${opt.id}`}
            aria-checked={selected===opt.id}
            aria-describedby={`storage-option-${opt.id}-description`}
            disabled={Boolean(opt.comingSoon)}
            tabIndex={selected===opt.id ? 0 : -1}
            onClick={()=>setSelected(opt.id)}
            onKeyDown={event=>handleStorageKeyDown(event, opt.id)}
            style={{
              background: opt.comingSoon ? "#F8FAFC" : selected===opt.id ? opt.bg : "white",
              border: `2px solid ${opt.comingSoon ? "#E2E8F0" : selected===opt.id ? opt.color : "#E2E8F0"}`,
              borderRadius:20, padding:"16px",
              cursor: opt.comingSoon ? "not-allowed" : "pointer",
              transition:"all 0.2s", position:"relative",
              opacity: opt.comingSoon ? 0.6 : 1,
              boxShadow: selected===opt.id ? `0 4px 20px ${opt.color}22` : "0 2px 8px rgba(0,0,0,0.05)",
              width:"100%", textAlign:"left", fontFamily:"inherit", appearance:"none"
            }}>

            {opt.recommended && (
              <div style={{position:"absolute",top:-10,right:16,background:opt.color,color:"white",fontSize:10,fontWeight:800,padding:"3px 10px",borderRadius:999,letterSpacing:0.5}}>
                ⭐ FREE-SAFE
              </div>
            )}
            {opt.comingSoon && (
              <div style={{position:"absolute",top:-10,right:16,background:"#64748B",color:"white",fontSize:10,fontWeight:800,padding:"3px 10px",borderRadius:999,letterSpacing:0.5}}>
                🔜 COMING SOON
              </div>
            )}

            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              {/* Radio */}
              <div style={{width:22,height:22,borderRadius:"50%",border:`2px solid ${opt.comingSoon?"#E2E8F0":selected===opt.id?opt.color:"#CBD5E1"}`,background:opt.comingSoon?"#F1F5F9":selected===opt.id?opt.color:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                {selected===opt.id && !opt.comingSoon && <div style={{width:8,height:8,borderRadius:"50%",background:"white"}}/>}
              </div>

              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:22}}>{opt.icon}</span>
                  <div>
                    <div style={{fontWeight:900,fontSize:15,color:"#1E1B4B"}}>{opt.title}</div>
                    <div style={{fontSize:11,color:opt.color,fontWeight:700}}>{opt.subtitle}</div>
                  </div>
                </div>
                <div id={`storage-option-${opt.id}-description`} style={{fontSize:12,color:"#64748B",lineHeight:1.6,marginBottom:10}}>{opt.desc}</div>

                {/* Pros & Cons */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                  {opt.pros.map(p=><div key={p} style={{fontSize:11,color:"#16A34A",fontWeight:600}}>{p}</div>)}
                  {opt.cons.map(c=><div key={c} style={{fontSize:11,color:"#94A3B8",fontWeight:600}}>{c}</div>)}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>


      {selected === "local" && (
        <div style={{width:"100%",maxWidth:420,background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:16,padding:"12px 14px",margin:"-10px 0 18px",boxShadow:"0 2px 10px rgba(251,146,60,0.12)"}}>
          <div style={{fontSize:12.5,fontWeight:900,color:"#C2410C",marginBottom:4}}>🔐 Important Local Mode PIN note</div>
          <div style={{fontSize:11.5,color:"#64748B",fontWeight:800,lineHeight:1.55}}>
            If you set a PIN, FinVault encrypts local data on this device. If you forget that PIN, the encrypted local data cannot be recovered by email. Keep a JSON backup safe for future recovery/export records. In-app restore is disabled for now.
          </div>
        </div>
      )}

      {error && (
        <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12,padding:"10px 16px",marginBottom:16,fontSize:13,color:"#DC2626",fontWeight:700,maxWidth:420,width:"100%"}}>
          ⚠️ {error}
        </div>
      )}

      <button onClick={handleContinue} disabled={!selected||loading}
        style={{
          width:"100%", maxWidth:420, padding:"17px", border:"none", borderRadius:16,
          cursor:selected?"pointer":"not-allowed", fontFamily:"inherit",
          fontWeight:900, fontSize:16,
          background: selected ? `linear-gradient(135deg,${STORAGE_OPTIONS.find(o=>o.id===selected)?.color||"#4F46E5"},${STORAGE_OPTIONS.find(o=>o.id===selected)?.color||"#4F46E5"}cc)` : "#E2E8F0",
          color: selected ? "white" : "#94A3B8",
          opacity: loading ? 0.7 : 1,
          transition:"all 0.2s"
        }}>
        {loading ? "Setting up…" : selected ? `Continue with ${STORAGE_OPTIONS.find(o=>o.id===selected)?.title} →` : "Select a storage option"}
      </button>

      <div style={{fontSize:11,color:"#94A3B8",marginTop:14,textAlign:"center",maxWidth:320,lineHeight:1.6}}>
        🆓 To keep FinVault free, use Local Mode by default and enable Cloud only when you need sync/backup.
      </div>
    </div>
  );
}
